import { useState, useEffect } from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js'
import { Bar } from 'react-chartjs-2'
import { get } from '../../../../utils/Api'
import { useGlobalContext } from '../../../../utils/Context'
import PageLoader from '../../../../components/loader/PageLoader'
import { Icon } from '@iconify/react'
import { useNavigate } from 'react-router-dom'
import './WeightDiscrepancy.scss'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler,
)

interface Summary {
  totalInvoices: number
  totalValue: number
  totalCollected: number
  totalOutstanding: number
}

interface StatusStat {
  count: number
  amount: number
}

interface PaymentStat {
  method: string
  count: number
  amount: number
}

interface DailyStat {
  _id: string
  totalInvoices: number
  totalAmount: number
  amountPaid: number
}

interface TopOffender {
  userId: string
  userModel: string
  totalDiscrepancies: number
  totalAmount: number
  totalPaid: number
  user: { name?: string; email?: string }
}

interface Analytics {
  summary: Summary
  statusStats: {
    draft: StatusStat
    sent: StatusStat
    paid: StatusStat
    overdue: StatusStat
  }
  paymentStats: PaymentStat[]
  dailyStats: DailyStat[]
  topOffenders: TopOffender[]
}

const chartOptions = {
  responsive: true,
  plugins: {
    legend: { position: 'top' as const, display: true },
  },
  scales: {
    y: {
      ticks: {
        callback: (value: any) =>
          '₦' + Number(value).toLocaleString(),
      },
    },
  },
}

function WeightDiscrepancyAnalytics() {
  const { notify } = useGlobalContext()
  const navigate = useNavigate()
  const [analytics, setAnalytics] = useState<Analytics | null>(null)
  const [loading, setLoading] = useState(true)

  // Date range
  const today = new Date().toISOString().split('T')[0]
  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
    .toISOString()
    .split('T')[0]
  const [startDate, setStartDate] = useState(thirtyDaysAgo)
  const [endDate, setEndDate] = useState(today)
  const [rangePreset, setRangePreset] = useState('30')

  const applyPreset = (preset: string) => {
    setRangePreset(preset)
    const now = new Date()
    const end = now.toISOString().split('T')[0]
    if (preset === 'custom') return
    const days = parseInt(preset)
    const start = new Date(now.getTime() - days * 24 * 60 * 60 * 1000)
      .toISOString()
      .split('T')[0]
    setStartDate(start)
    setEndDate(end)
  }

  function fetchAnalytics(start: string, end: string) {
    setLoading(true)
    get(`admin/weight-discrepancy/analytics?startDate=${start}&endDate=${end}`)
      .then((res: any) => {
        if (res.status === 200) {
          setAnalytics(res.data)
        } else {
          notify('error', res.message || 'Failed to fetch analytics')
        }
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
        notify('error', 'Failed to fetch analytics')
      })
  }

  useEffect(() => {
    fetchAnalytics(startDate, endDate)
  }, [startDate, endDate])

  const formatCurrency = (amount: number) =>
    '₦' + Number(amount || 0).toLocaleString()

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-NG', {
      month: 'short',
      day: 'numeric',
    })

  // ─── Chart data ───────────────────────────────────────────────────────────

  const dailyLabels = analytics?.dailyStats.map((d) => formatDate(d._id)) ?? []

  const amountChartData = {
    labels: dailyLabels,
    datasets: [
      {
        label: 'Total Billed',
        data: analytics?.dailyStats.map((d) => d.totalAmount) ?? [],
        backgroundColor: '#446ce4cc',
        borderColor: '#446ce4',
        borderWidth: 1,
      },
      {
        label: 'Collected',
        data: analytics?.dailyStats.map((d) => d.amountPaid) ?? [],
        backgroundColor: '#4caf50cc',
        borderColor: '#4caf50',
        borderWidth: 1,
      },
    ],
  }

  const invoiceChartData = {
    labels: dailyLabels,
    datasets: [
      {
        label: 'Invoices Raised',
        data: analytics?.dailyStats.map((d) => d.totalInvoices) ?? [],
        backgroundColor: '#ff9800cc',
        borderColor: '#ff9800',
        borderWidth: 1,
      },
    ],
  }

  const invoiceChartOptions = {
    responsive: true,
    plugins: { legend: { position: 'top' as const, display: true } },
  }

  // ─── Status cards config ──────────────────────────────────────────────────

  const statusCards = [
    {
      key: 'draft',
      label: 'Draft',
      icon: 'mdi:file-document-outline',
      color: '#666',
      bg: '#f5f5f5',
    },
    {
      key: 'sent',
      label: 'Sent',
      icon: 'mdi:send',
      color: '#1565c0',
      bg: '#e3f2fd',
    },
    {
      key: 'paid',
      label: 'Paid',
      icon: 'mdi:check-circle',
      color: '#2e7d32',
      bg: '#e8f5e9',
    },
    {
      key: 'overdue',
      label: 'Overdue',
      icon: 'mdi:alert-circle',
      color: '#c62828',
      bg: '#ffebee',
    },
  ]

  if (loading) {
    return (
      <div className='home'>
        <PageLoader />
      </div>
    )
  }

  const s = analytics?.summary

  return (
    <div className='home'>
      {/* Page header */}
      <div className='home_tag flex justify-between' style={{ flexWrap: 'wrap', gap: '10px' }}>
        <h2>Weight Discrepancy Analytics</h2>
        <button
          onClick={() => navigate('/admin/weight-discrepancy')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 16px',
            backgroundColor: '#446ce4',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
          }}
        >
          <Icon icon='mdi:arrow-left' /> Back to Invoices
        </button>
      </div>

      {/* Date range filter */}
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '10px',
          padding: '16px 20px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          flexWrap: 'wrap',
        }}
      >
        <Icon icon='mdi:calendar-range' style={{ color: '#446ce4', fontSize: '20px' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '14px', color: '#666' }}>Range:</span>
          {[
            { label: '7 days', value: '7' },
            { label: '30 days', value: '30' },
            { label: '90 days', value: '90' },
            { label: 'Custom', value: 'custom' },
          ].map((p) => (
            <button
              key={p.value}
              onClick={() => applyPreset(p.value)}
              style={{
                padding: '5px 12px',
                borderRadius: '20px',
                border: rangePreset === p.value ? '2px solid #446ce4' : '1px solid #ddd',
                backgroundColor: rangePreset === p.value ? '#e8eaf6' : '#fff',
                color: rangePreset === p.value ? '#446ce4' : '#333',
                cursor: 'pointer',
                fontSize: '13px',
              }}
            >
              {p.label}
            </button>
          ))}
        </div>

        {rangePreset === 'custom' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <label style={{ fontSize: '13px', color: '#666' }}>
              From:&nbsp;
              <input
                type='date'
                value={startDate}
                max={endDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: '4px' }}
              />
            </label>
            <label style={{ fontSize: '13px', color: '#666' }}>
              To:&nbsp;
              <input
                type='date'
                value={endDate}
                min={startDate}
                max={today}
                onChange={(e) => setEndDate(e.target.value)}
                style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: '4px' }}
              />
            </label>
          </div>
        )}

        <span style={{ marginLeft: 'auto', fontSize: '13px', color: '#999' }}>
          {startDate} → {endDate}
        </span>
      </div>

      {/* Summary cards */}
      <div className='home-head' style={{ marginBottom: '24px' }}>
        <div className='biz flex justify-between flex-wrap'>
          {[
            {
              label: 'Total Invoices',
              value: s?.totalInvoices.toLocaleString() ?? '0',
              icon: 'mdi:file-document-multiple',
              color: '#446ce4',
            },
            {
              label: 'Total Billed',
              value: formatCurrency(s?.totalValue ?? 0),
              icon: 'mdi:cash-multiple',
              color: '#ff9800',
            },
            {
              label: 'Total Collected',
              value: formatCurrency(s?.totalCollected ?? 0),
              icon: 'mdi:cash-check',
              color: '#4caf50',
            },
            {
              label: 'Outstanding',
              value: formatCurrency(s?.totalOutstanding ?? 0),
              icon: 'mdi:cash-clock',
              color: '#f44336',
            },
          ].map((card) => (
            <div className='card' key={card.label}>
              <p className='flex items-center' style={{ gap: '6px' }}>
                <Icon icon={card.icon} style={{ color: card.color, fontSize: '18px' }} />
                {card.label}
              </p>
              <h2 style={{ color: card.color }}>{card.value}</h2>
            </div>
          ))}
        </div>
      </div>

      {/* Status breakdown */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '16px',
          marginBottom: '24px',
        }}
      >
        {statusCards.map((sc) => {
          const stat = analytics?.statusStats[sc.key as keyof typeof analytics.statusStats]
          return (
            <div
              key={sc.key}
              style={{
                backgroundColor: sc.bg,
                borderRadius: '10px',
                padding: '16px 20px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <Icon icon={sc.icon} style={{ color: sc.color, fontSize: '22px' }} />
                <span style={{ fontWeight: 600, color: sc.color, textTransform: 'capitalize' }}>
                  {sc.label}
                </span>
              </div>
              <p style={{ fontSize: '24px', fontWeight: 700, color: sc.color, margin: 0 }}>
                {stat?.count ?? 0}
              </p>
              <p style={{ fontSize: '13px', color: '#666', marginTop: '4px' }}>
                {formatCurrency(stat?.amount ?? 0)}
              </p>
            </div>
          )
        })}
      </div>

      {/* Charts row */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr',
          gap: '20px',
          marginBottom: '24px',
          alignItems: 'start',
        }}
      >
        {/* Daily amount chart */}
        <div
          style={{
            backgroundColor: '#fff',
            borderRadius: '10px',
            padding: '25px 30px',
          }}
        >
          <div style={{ paddingBottom: '16px' }}>
            <h2 style={{ fontSize: '16px', fontWeight: 600 }}>Daily Billing vs Collections</h2>
            <p style={{ fontSize: '13px', color: '#999', margin: '4px 0 0' }}>
              Amount billed and collected per day
            </p>
          </div>
          <Bar options={chartOptions} data={amountChartData} />
        </div>

        {/* Payment methods */}
        <div
          style={{
            backgroundColor: '#fff',
            borderRadius: '10px',
            padding: '25px 30px',
          }}
        >
          <h2 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '20px' }}>
            Payment Methods
          </h2>
          {analytics?.paymentStats.length ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {analytics.paymentStats.map((p) => {
                const total = analytics.paymentStats.reduce((sum, x) => sum + x.amount, 0)
                const pct = total > 0 ? Math.round((p.amount / total) * 100) : 0
                return (
                  <div key={p.method}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <span style={{ fontSize: '14px', textTransform: 'capitalize' }}>
                        {p.method === 'wallet' ? '💳 Wallet' : '🏦 Manual (Admin)'}
                      </span>
                      <span style={{ fontSize: '13px', fontWeight: 600 }}>{pct}%</span>
                    </div>
                    <div style={{ backgroundColor: '#f0f0f0', borderRadius: '4px', height: '8px' }}>
                      <div
                        style={{
                          width: `${pct}%`,
                          backgroundColor: p.method === 'wallet' ? '#446ce4' : '#4caf50',
                          borderRadius: '4px',
                          height: '8px',
                        }}
                      />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                      <span style={{ fontSize: '12px', color: '#999' }}>{p.count} invoices</span>
                      <span style={{ fontSize: '12px', color: '#999' }}>{formatCurrency(p.amount)}</span>
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <p style={{ color: '#999', fontSize: '14px', textAlign: 'center', marginTop: '30px' }}>
              No payment data
            </p>
          )}
        </div>
      </div>

      {/* Daily invoice count chart */}
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '10px',
          padding: '25px 30px',
          marginBottom: '24px',
        }}
      >
        <div style={{ paddingBottom: '16px' }}>
          <h2 style={{ fontSize: '16px', fontWeight: 600 }}>Daily Invoice Volume</h2>
          <p style={{ fontSize: '13px', color: '#999', margin: '4px 0 0' }}>
            Number of invoices raised per day
          </p>
        </div>
        <Bar options={invoiceChartOptions} data={invoiceChartData} />
      </div>

      {/* Top offenders table */}
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '10px',
          padding: '25px 30px',
          overflowX: 'auto',
        }}
      >
        <h2 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '20px' }}>
          <Icon
            icon='mdi:alert-circle-outline'
            style={{ verticalAlign: 'middle', marginRight: '8px', color: '#f44336' }}
          />
          Top Offenders
          <span style={{ fontSize: '13px', fontWeight: 400, color: '#999', marginLeft: '8px' }}>
            customers with most discrepancies
          </span>
        </h2>

        {analytics?.topOffenders.length ? (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f5f5f5' }}>
                {['#', 'Customer', 'Type', 'Discrepancies', 'Total Billed', 'Total Paid', 'Outstanding', ''].map(
                  (h) => (
                    <th
                      key={h}
                      style={{
                        padding: '10px 12px',
                        textAlign: 'left',
                        fontSize: '13px',
                        fontWeight: 600,
                        color: '#666',
                      }}
                    >
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {analytics.topOffenders.map((offender, i) => {
                const outstanding = offender.totalAmount - offender.totalPaid
                return (
                  <tr
                    key={offender.userId}
                    style={{ borderBottom: '1px solid #f0f0f0' }}
                  >
                    <td style={{ padding: '12px', fontSize: '13px', color: '#999' }}>{i + 1}</td>
                    <td style={{ padding: '12px' }}>
                      <p style={{ fontSize: '14px', fontWeight: 500, margin: 0 }}>
                        {offender.user?.name || 'Unknown'}
                      </p>
                      <p style={{ fontSize: '12px', color: '#999', margin: '2px 0 0' }}>
                        {offender.user?.email}
                      </p>
                    </td>
                    <td style={{ padding: '12px', fontSize: '13px' }}>{offender.userModel}</td>
                    <td style={{ padding: '12px' }}>
                      <span
                        style={{
                          backgroundColor: '#ffebee',
                          color: '#c62828',
                          padding: '3px 10px',
                          borderRadius: '12px',
                          fontSize: '13px',
                          fontWeight: 600,
                        }}
                      >
                        {offender.totalDiscrepancies}
                      </span>
                    </td>
                    <td style={{ padding: '12px', fontSize: '13px' }}>
                      {formatCurrency(offender.totalAmount)}
                    </td>
                    <td style={{ padding: '12px', fontSize: '13px', color: '#4caf50' }}>
                      {formatCurrency(offender.totalPaid)}
                    </td>
                    <td
                      style={{
                        padding: '12px',
                        fontSize: '13px',
                        fontWeight: 600,
                        color: outstanding > 0 ? '#f44336' : '#4caf50',
                      }}
                    >
                      {formatCurrency(outstanding)}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span
                        className='view'
                        onClick={() =>
                          navigate(
                            `/admin/${offender.userModel === 'Business' ? 'business' : 'customer'}/${offender.userId}`,
                          )
                        }
                      >
                        view
                      </span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
            <Icon icon='mdi:account-check' style={{ fontSize: '48px', marginBottom: '12px' }} />
            <p>No offenders in this period</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default WeightDiscrepancyAnalytics
